import Api from '../api';
import { cachedGet, invalidateCache, peekGet } from '../request_cache';

export interface HiringHistoryItemResponse {
  key: string;
  label: string;
  interesse: boolean;
  licitacoes: number;
  linhas: number;
  valor_medio: number | null;
  valor_mediano: number | null;
  gasto_total: number | null;
  ultima_compra: string | null;
}

export interface HiringHistoryResponse {
  orgao: { id: number | null; nome: string | null };
  total: number;
  page: number;
  per_page: number;
  items: HiringHistoryItemResponse[];
}

export interface HiringHistoryParams {
  page?: number;
  perPage?: number;
  search?: string;
}

const listRequest = (biddingId: number, params: HiringHistoryParams) =>
  [
    `/gestao_ia/licitacoes/${biddingId}/historico_contratacoes`,
    {
      params: {
        page: params.page,
        per_page: params.perPage,
        q: params.search || undefined,
      },
    },
  ] as const;

export const listHiringHistory = (biddingId: number, params: HiringHistoryParams = {}) =>
  cachedGet<HiringHistoryResponse>(...listRequest(biddingId, params));

export const peekHiringHistory = (biddingId: number, params: HiringHistoryParams = {}) =>
  peekGet<HiringHistoryResponse>(...listRequest(biddingId, params));

export interface HiringHistoryBiddingResponse {
  bidding_id: number;
  edital: string | null;
  data: string | null;
  participantes: number;
  valor: number | null;
  quantidade: number | null;
  vencedor: string | null;
  descricao: string | null;
  adjudicado: boolean;
}

export interface HiringHistoryBiddingsResponse {
  item: string;
  items: HiringHistoryBiddingResponse[];
}

const biddingsRequest = (biddingId: number, item: string) =>
  [
    `/gestao_ia/licitacoes/${biddingId}/historico_contratacoes/licitacoes`,
    { params: { item } },
  ] as const;

export const listHiringHistoryBiddings = (biddingId: number, item: string) =>
  cachedGet<HiringHistoryBiddingsResponse>(...biddingsRequest(biddingId, item));

export const peekHiringHistoryBiddings = (biddingId: number, item: string) =>
  peekGet<HiringHistoryBiddingsResponse>(...biddingsRequest(biddingId, item));

export interface HiringHistoryDrawerBidding {
  id: number;
  edital: string | null;
  objeto: string | null;
  processo: string | null;
  data: string | null;
  abertura: string | null;
  situacao: string | null;
  orgao: string | null;
  uasg: string | null;
  cidade: string | null;
  endereco: string | null;
}

export interface HiringHistoryWinner {
  participante: string | null;
  marca: string | null;
  modelo: string | null;
  quantidade: number | null;
  valor: number | null;
  reducao_proposta: number | null;
  reducao_lance: number | null;
}

export interface HiringHistoryParticipant {
  posicao: string | null;
  participante: string | null;
  cnpj: string | null;
  proposta_inicial: number | null;
  lance_final: number | null;
  valor_final: number | null;
  reducao: number | null;
  adjudicado: boolean;
  desclassificada: boolean;
  inabilitada: boolean;
  motivo: string | null;
  me_epp: boolean;
}

export interface HiringHistoryEditalItem {
  descricao: string | null;
  quantidade: number | null;
  unidade: string | null;
  valor_unitario: number | null;
  valor_total: number | null;
  valor_estimado: number | null;
}

export interface HiringHistoryDrawerItem {
  numero_lote: string | null;
  numero_item: string | null;
  label: string | null;
  descricao: string | null;
  quantidade: number | null;
  edital: HiringHistoryEditalItem | null;
  economia: number | null;
  vencedores: HiringHistoryWinner[];
  participantes: HiringHistoryParticipant[];
}

export interface HiringHistoryDrawerResponse {
  licitacao: HiringHistoryDrawerBidding;
  itens: HiringHistoryDrawerItem[];
}

const detailRequest = (biddingId: number, historicBiddingId: number, item: string) =>
  [
    `/gestao_ia/licitacoes/${biddingId}/historico_contratacoes/detalhe`,
    { params: { bidding: historicBiddingId, item } },
  ] as const;

export const getHiringHistoryDetail = (
  biddingId: number,
  historicBiddingId: number,
  item: string,
) => cachedGet<HiringHistoryDrawerResponse>(...detailRequest(biddingId, historicBiddingId, item));

export const peekHiringHistoryDetail = (
  biddingId: number,
  historicBiddingId: number,
  item: string,
) => peekGet<HiringHistoryDrawerResponse>(...detailRequest(biddingId, historicBiddingId, item));

const invalidateItemLists = () =>
  invalidateCache(
    (url) => url.endsWith('/historico_contratacoes') || url.endsWith('/possiveis_concorrentes'),
  );

export const markHiringHistoryInterest = (
  biddingId: number,
  items: Array<{ key: string; label: string }>,
) =>
  Api.post(`/gestao_ia/licitacoes/${biddingId}/historico_contratacoes/interesses`, {
    item_keys: items.map((item) => item.key),
    labels: items.reduce<Record<string, string>>((acc, item) => {
      acc[item.key] = item.label;
      return acc;
    }, {}),
  }).then((response) => {
    invalidateItemLists();
    return response;
  });

export const unmarkHiringHistoryInterest = (biddingId: number, itemKeys: string[]) =>
  Api.delete(`/gestao_ia/licitacoes/${biddingId}/historico_contratacoes/interesses`, {
    data: { item_keys: itemKeys },
  }).then((response) => {
    invalidateItemLists();
    return response;
  });

export type OcorrenciaTipo = 'esclarecimento' | 'impugnacao' | 'aviso' | 'recurso' | 'diligencia';

export interface OcorrenciaLado {
  titulo: string | null;
  descricao: string | null;
  data: string | null;
}

export interface OcorrenciaResponse {
  tipo: OcorrenciaTipo;
  empresa: string | null;
  cnpj: string | null;
  urls: string[];
  resumo: string | null;
  dialogo: { pergunta?: OcorrenciaLado; resposta?: OcorrenciaLado } | null;
}

export interface OcorrenciasResponse {
  pendente: boolean;
  ocorrencias: OcorrenciaResponse[];
}

export const listHiringHistoryOcorrencias = (biddingId: number, historicBiddingId: number) =>
  Api.get<OcorrenciasResponse>(
    `/gestao_ia/licitacoes/${biddingId}/historico_contratacoes/ocorrencias`,
    { params: { bidding: historicBiddingId } },
  );

export const downloadOcorrenciaZip = (
  biddingId: number,
  historicBiddingId: number,
  indice: number,
) =>
  Api.get<Blob>(`/gestao_ia/licitacoes/${biddingId}/historico_contratacoes/ocorrencias/arquivos`, {
    params: { bidding: historicBiddingId, indice },
    responseType: 'blob',
  });

export interface ParticipantFilesSummary {
  documentos: string[];
  contratos: string[];
}

const participantFilesRequest = (biddingId: number, historicBiddingId: number) =>
  [
    `/gestao_ia/licitacoes/${biddingId}/arquivos_participantes`,
    { params: { bidding: historicBiddingId } },
  ] as const;

export const getParticipantFilesSummary = (biddingId: number, historicBiddingId: number) =>
  cachedGet<ParticipantFilesSummary>(...participantFilesRequest(biddingId, historicBiddingId));

export const peekParticipantFilesSummary = (biddingId: number, historicBiddingId: number) =>
  peekGet<ParticipantFilesSummary>(...participantFilesRequest(biddingId, historicBiddingId));

const participantFilesHref = (
  kind: 'documentos' | 'contratos',
  biddingId: number,
  historicBiddingId: number,
  cnpj: string,
) =>
  `${
    process.env.REACT_APP_API_ENDPOINT
  }/gestao_ia/licitacoes/${biddingId}/arquivos_participantes/${kind}?bidding=${historicBiddingId}&cnpj=${encodeURIComponent(
    cnpj,
  )}`;

export const participantDocumentsHref = (
  biddingId: number,
  historicBiddingId: number,
  cnpj: string,
) => participantFilesHref('documentos', biddingId, historicBiddingId, cnpj);

export const participantContractsHref = (
  biddingId: number,
  historicBiddingId: number,
  cnpj: string,
) => participantFilesHref('contratos', biddingId, historicBiddingId, cnpj);
